<template>
  <div class="mentee-review-status-pie">
    <div id="mentee-review-status-pie-chart">
      <apexchart v-if="hasData" type="donut" :height="220" :options="pieChartOptions" :series="series" />
      <div v-else class="empty-state text-center">No mentor data available</div>
    </div>
    <div v-if="hasData" class="chart-legend">
      <div class="legend-item">
        <span class="legend-swatch swatch-low"></span>
        <span>Reviewed ({{ totalMentorReviewedCount }})</span>
      </div>
      <div class="legend-item">
        <span class="legend-swatch swatch-mid"></span>
        <span>Pending ({{ totalMentorReviewCount }})</span>
      </div>
    </div>
    <portal to="chart-tooltip-root">
      <div
        v-if="tooltip.show"
        class="mentor-tooltip"
        :style="{ left: tooltip.left + 'px', top: tooltip.top + 'px' }"
      >
        <div class="title" :style="{ color: tooltip.color }">{{ tooltip.sliceName }}</div>
        <div class="header">
          <span style="font-weight: bold;">Mentor</span>
          <span class="reviewed">Reviewed</span>
          <span class="pending">Pending</span>
        </div>
        <div
          v-for="loc in locationDataMentorReview"
          :key="loc.name"
          class="row"
        >
          <span>{{ truncate(loc.name) }}</span>
          <span class="reviewed">{{ loc.reviewed }}</span>
          <span class="pending">{{ loc.pending }}</span>
        </div>
      </div>
    </portal>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

const TOOLTIP_W = 240;
const ROW_H = 22;
const HEADER_H = 70;


export default {
  props: {
    totalMentorReviewCount: {
      type: Number,
      default: 0,
    },
    totalMentorReviewedCount: {
      type: Number,
      default: 0,
    },
    locationDataMentorReview: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      tooltip: { show: false, left: 0, top: 0, sliceName: '', color: '' },
    };
  },
  methods: {
    truncate(text) {
      if(text.length > 25) {
        return  text.substring(0, 25) + '...';
      } else {
        return  text;
      }
    },
    onSliceEnter(event, chartContext, { dataPointIndex }) {
      const isReviewed = dataPointIndex === 0;
      this.tooltip.sliceName = isReviewed ? 'Reviewed' : 'Pending';
      this.tooltip.color = isReviewed ? '#00ACEE' : '#EE4200';
      this.tooltip.show = true;
      this.positionTooltip(event);
    },
    onMouseMove(event) {
      if (this.tooltip.show) this.positionTooltip(event);
    },
    onSliceLeave() {
      this.tooltip.show = false;
    },
    positionTooltip(event) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const cursorX = event.clientX;
      const cursorY = event.clientY;
      const estHeight = HEADER_H + this.locationDataMentorReview.length * ROW_H;
      let left = cursorX + 14;
      let top  = cursorY + 14;
      // right-edge → flip to left of cursor
      if (left + TOOLTIP_W > vw - 8) left = cursorX - TOOLTIP_W - 14;
      // left-edge clamp (covers the just-flipped case + narrow viewports)
      left = Math.max(8, Math.min(left, vw - TOOLTIP_W - 8));
      // bottom-edge → push up
      if (top + estHeight > vh - 8) top = Math.max(8, vh - Math.min(estHeight, vh - 16) - 8);
      this.tooltip.left = left;
      this.tooltip.top  = top;
    },

  },
  computed: {
    series() {
      return [this.totalMentorReviewedCount, this.totalMentorReviewCount];
    },
    hasData() {
      return this.series.length > 0 && this.series.some(v => v > 0);
    },
    seriesToolTip() {
      return [
        {
          title: 'Reviewed',
          value: this.totalMentorReviewedCount,
          color: '#00ACEE',
        },
        {
          title: 'Pending',
          value: this.totalMentorReviewCount,
          color: '#EE4200',
        }
      ];
    },
    pieChartOptions() {
      const tooltipGroups = this.seriesToolTip;
      const locationDataOptions = this.locationDataMentorReview;
      return {
        chart: {
          type: 'donut',
          events: {
            dataPointMouseEnter: this.onSliceEnter,
            dataPointMouseLeave: this.onSliceLeave,
            mouseMove: this.onMouseMove,
          },
          toolbar: {
            show: false,
          },
        },
        labels: ['Reviewed', 'Pending'],
        colors: ['#00ACEE', '#EE4200'],
        stroke: {
          width: 3,
          colors: ['#ffffff'],
        },
        dataLabels: {
          enabled: true,
          formatter(value, context) {
            const count = context.w.config.series[context.seriesIndex];
            return count > 0 ? count : '';
          },
          offset: 28,
          style: {
            fontSize: '18px',
            fontWeight: 500,
            colors: ['#374151'],
          },
          dropShadow: {
            enabled: false,
          },
        },
        plotOptions: {
          pie: {
            customScale: 0.8,
            expandOnClick: false,
            dataLabels: {
              offset: 32,
              minAngleToShowLabel: 10,
            },
            donut: {
              size: '65%',
            },
          },
        },
        legend: {
          show: false,
        },
        fill: {
          opacity: 1
        },
        states: {
          hover: {
            filter: {
              type: 'none',
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        tooltip: {
          enabled: true,
          custom: () => '',
        },
      }
    },

  },
};
</script>

<style scoped>
.mentee-review-status-pie {
  padding: 8px 12px 0;
}
.chart-legend {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #1f2937;
}

.legend-item span {
  font-size: 13px;
}

.legend-swatch {
  width: 12px;
  height: 12px;
  margin-right: 10px;
  border-radius: 50%;
  display: inline-block;
}

.swatch-low {
  background: #00ACEE;
}

.swatch-mid {
  background: #EE4200;
}

.swatch-high {
  background: #16A34A;
}
.mentee-review-status-pie {
  max-height: calc(100vh - 16px);
  overflow-y: visible;
  scrollbar-width: thin;
}

.mentor-tooltip {
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  min-width: 200px;
  max-width: 380px;
  max-height: calc(100vh - 10px);
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 12px;
}
.mentor-tooltip .title  { font-weight: 600; font-size: 13px; margin-bottom: 8px; }
.mentor-tooltip .header { display: grid; grid-template-columns: 1fr 60px 60px;
                          font-size: 11px; color: #9CA3AF;
                          border-bottom: 1px solid #e5e7eb;
                          padding-bottom: 4px; margin-bottom: 4px; }
.mentor-tooltip .row    { display: grid; grid-template-columns: 1fr 60px 60px;
                          padding: 2px 15px; color: #374151; }
.mentor-tooltip .row .reviewed { color: #00ACEE; font-weight: 600; text-align: right; }
.mentor-tooltip .row .pending  { color: #EE4200; font-weight: 600; text-align: right; }
.mentor-tooltip .header .reviewed { font-weight: 600; text-align: right; }
.mentor-tooltip .header .pending  { font-weight: 600; text-align: right; }

</style>



// WEBPACK FOOTER //
// src/components/manager/mentee_review_status_piechart.vue